import { createAction, createReducer } from '@reduxjs/toolkit';
import { Blueprint, Transaction } from 'types/rest/assetCatalogTypes';
import { BlueprintDraft, BlueprintSourceFilter, BlueprintsState, BlueprintViewMode } from './blueprintsTypes';

export const initialBlueprintsState: BlueprintsState = {
  blueprints: [],
  availableTransactions: [],
  isLoading: false,
  detailLoading: false,
  viewMode: 'GRID',
  filter: { source: 'ALL', searchTerm: '', tags: [] },
  editor: { open: false, draft: null },
  applyBlueprintId: null,
  detailBlueprintId: null,
  page: 1,
  pageSize: 24,
  total: 0,
};

export const setBlueprintsLoading = createAction<boolean>('blueprints/setLoading');
export const setBlueprints = createAction<Blueprint[]>('blueprints/setBlueprints');
export const setAvailableTransactions = createAction<Transaction[]>('blueprints/setAvailableTransactions');
export const setViewMode = createAction<BlueprintViewMode>('blueprints/setViewMode');
export const setSourceFilter = createAction<BlueprintSourceFilter>('blueprints/setSourceFilter');
export const setSearchTerm = createAction<string>('blueprints/setSearchTerm');
export const toggleTagFilter = createAction<string>('blueprints/toggleTagFilter');
export const clearFilters = createAction('blueprints/clearFilters');
export const setPage = createAction<number>('blueprints/setPage');
export const setPageSize = createAction<number>('blueprints/setPageSize');
export const setTotal = createAction<number>('blueprints/setTotal');
export const openEditor = createAction<BlueprintDraft>('blueprints/openEditor');
export const closeEditor = createAction('blueprints/closeEditor');
export const openApply = createAction<string>('blueprints/openApply');
export const closeApply = createAction('blueprints/closeApply');
export const setDetailLoading = createAction<boolean>('blueprints/setDetailLoading');
export const openDetailDrawer = createAction<string>('blueprints/openDetailDrawer');
export const closeDetailDrawer = createAction('blueprints/closeDetailDrawer');
export const upsertBlueprint = createAction<Blueprint>('blueprints/upsertBlueprint');
export const removeBlueprint = createAction<string>('blueprints/removeBlueprint');

const blueprintsReducer = createReducer(initialBlueprintsState, builder => {
  builder
    .addCase(setBlueprintsLoading, (state, action) => {
      state.isLoading = action.payload;
    })
    .addCase(setBlueprints, (state, action) => {
      state.blueprints = action.payload;
    })
    .addCase(setAvailableTransactions, (state, action) => {
      state.availableTransactions = action.payload;
    })
    .addCase(setViewMode, (state, action) => {
      state.viewMode = action.payload;
      state.pageSize = action.payload === 'LIST' ? 20 : 24;
      state.page = 1;
    })
    .addCase(setSourceFilter, (state, action) => {
      state.filter.source = action.payload;
      state.page = 1;
    })
    .addCase(setSearchTerm, (state, action) => {
      state.filter.searchTerm = action.payload;
      state.page = 1;
    })
    .addCase(toggleTagFilter, (state, action) => {
      const tag = action.payload;
      state.filter.tags = state.filter.tags.includes(tag)
        ? state.filter.tags.filter(t => t !== tag)
        : [...state.filter.tags, tag];
      state.page = 1;
    })
    .addCase(clearFilters, state => {
      state.filter = { source: 'ALL', searchTerm: '', tags: [] };
      state.page = 1;
    })
    .addCase(setPage, (state, action) => {
      state.page = action.payload;
    })
    .addCase(setPageSize, (state, action) => {
      state.pageSize = action.payload;
    })
    .addCase(setTotal, (state, action) => {
      state.total = action.payload;
    })
    .addCase(openEditor, (state, action) => {
      state.editor = { open: true, draft: action.payload };
    })
    .addCase(closeEditor, state => {
      state.editor = { open: false, draft: null };
    })
    .addCase(openApply, (state, action) => {
      state.applyBlueprintId = action.payload;
    })
    .addCase(closeApply, state => {
      state.applyBlueprintId = null;
    })
    .addCase(setDetailLoading, (state, action) => {
      state.detailLoading = action.payload;
    })
    .addCase(openDetailDrawer, (state, action) => {
      state.detailBlueprintId = action.payload;
    })
    .addCase(closeDetailDrawer, state => {
      state.detailBlueprintId = null;
    })
    .addCase(upsertBlueprint, (state, action) => {
      const idx = state.blueprints.findIndex(b => b.id === action.payload.id);
      if (idx >= 0) state.blueprints[idx] = action.payload;
      else state.blueprints.unshift(action.payload);
    })
    .addCase(removeBlueprint, (state, action) => {
      state.blueprints = state.blueprints.filter(b => b.id !== action.payload);
    });
});

export default blueprintsReducer;
